import path from "node:path";
import type { Locator, Response } from "@iflyrpa/share";
import { response, success } from "@iflyrpa/share";
import { TimeFormatter } from "../../utils";
import type { PublishVideoAction } from "./mock";
import { buildDescription } from "./payload";
import { buildVideoMetaFromParams } from "./videoMeta";
import { validateShipinhaoTitle, validateShipinhaoVideo } from "./videoValidator";

/**
 * 视频号视频发布 - RPA 方式
 * 通过浏览器自动化实现视频号视频发布
 */
export const rpaAction: PublishVideoAction = async (task, params) => {
    task.logger.info("开始微信视频号视频发布（RPA 模式）");

    // 校验平台限制（时长 8 小时 / 大小 20GB / MP4+H.264），开浏览器前先拦截
    // 宽高时长用调用方传入值，编码格式从文件分段读取
    const videoMeta = buildVideoMetaFromParams(params.videoPath, params.videoMetadata);
    task.logger.info(
        `视频: ${videoMeta.width}×${videoMeta.height}, ${videoMeta.duration.toFixed(2)}s, ${(videoMeta.fileSize / 1024 / 1024).toFixed(2)}MB, codec=${videoMeta.codec || "unknown"}`,
    );
    if (!videoMeta.codec) {
        task.logger.warn("未能读取视频编码格式，跳过 H.264 预检，交由服务端判断");
    }

    const validationError = validateShipinhaoVideo(params.videoPath, videoMeta);
    if (validationError) {
        task.logger.error(`视频校验未通过: ${validationError}`);
        return response(414, validationError, "");
    }
    task.logger.info("视频校验通过");

    const titleError = validateShipinhaoTitle(params.title);
    if (titleError) {
        task.logger.error(`标题校验未通过: ${titleError}`);
        return response(414, titleError, "");
    }

    // RPA 走页面表单，以下参数没有对应的稳定交互，只在 mockApi 模式生效
    const unsupported = [
        params.verticalCoverPath && "verticalCoverPath",
        params.collection && "collection",
        params.originalFlag && "originalFlag",
        params.postWithMemberZoneLink && "postWithMemberZoneLink",
    ].filter(Boolean);
    if (unsupported.length) {
        task.logger.warn(`RPA 模式不支持以下参数，将被忽略: ${unsupported.join("、")}；如需生效请使用 mockApi 模式`);
    }

    // tagInfo 部分支持：tagType=5 暂不支持省市选择，其他类型完全支持
    if (params.tagInfo && params.tagInfo.tagType === 5) {
        const shootInfo = (params.tagInfo as any).shootInfo;
        if (shootInfo && (shootInfo.provinceCode || shootInfo.cityCode)) {
            task.logger.warn("tagInfo.tagType=5 在 RPA 模式下仅支持拍摄时间和国家选择，省市选择需要使用 mockApi 模式");
        }
    }

    const tmpCachePath = task.getTmpPath();

    const page = await task.createPage({
        url: "https://channels.weixin.qq.com/platform/post/create",
        show: task.debug,
        cookies: params.cookies,
    });

    try {
        const waitForElement = async (selector: string, timeout = 10000): Promise<Locator | null> => {
            try {
                const element = page.locator(selector);
                await element.waitFor({ state: "visible", timeout });
                return element;
            } catch {
                task.logger.warn(`元素未找到: ${selector}`);
                return null;
            }
        };

        // 重试执行
        const retryAction = async <T>(action: () => Promise<T>, maxRetries = 3, delay = 1000): Promise<T> => {
            let lastError: Error | undefined;
            for (let i = 0; i < maxRetries; i++) {
                try {
                    return await action();
                } catch (error) {
                    lastError = error as Error;
                    task.logger.warn(`重试 ${i + 1}/${maxRetries}: ${lastError.message}`);
                    if (i < maxRetries - 1) {
                        await page.waitForTimeout(delay);
                    }
                }
            }
            throw lastError;
        };

        task.logger.info("等待页面加载...");

        // 检查登录状态
        task.logger.info("检查登录状态...");

        if (task.debug) {
            task.logger.info(`当前页面 URL: ${page.url()}`);
            const title = await page.title();
            task.logger.info(`页面标题: ${title}`);
        }

        try {
            await page.waitForSelector(".post-edit-wrap", { state: "visible", timeout: 30_000 });
            task.logger.info("✓ 登录状态正常，找到编辑器容器");
        } catch {
            task.logger.error("✗ 未找到编辑器，可能登录失效");
            return { code: 414, message: "登录失效或页面加载异常", data: page.url() };
        }

        task.logger.info("页面加载完成，开始填充内容");

        // 上传视频
        task.logger.info("开始上传视频");
        await retryAction(async () => {
            // 定位视频上传输入框
            const videoUploadSelectors = [
                '.ant-upload-btn input[type="file"][accept*="video"]',
                '.upload input[type="file"][accept*="video"]',
                'input[type="file"][accept*="video"]',
            ];

            let uploadInput: Locator | null = null;

            if (task.debug) {
                const allFileInputs = await page.locator('input[type="file"]').count();
                task.logger.info(`页面中共找到 ${allFileInputs} 个文件上传输入框`);
            }

            for (const selector of videoUploadSelectors) {
                const input = page.locator(selector);
                const count = await input.count();
                if (count > 0) {
                    uploadInput = input.first();
                    task.logger.info(`找到视频上传输入框: ${selector}`);
                    break;
                }
            }

            if (!uploadInput) {
                task.logger.warn("未找到视频上传输入框，等待 3 秒后重试...");
                await page.waitForTimeout(3000);

                const anyFileInput = page.locator('input[type="file"]');
                const inputCount = await anyFileInput.count();

                if (inputCount > 0) {
                    uploadInput = anyFileInput.first();
                    task.logger.info(`找到文件输入框（共 ${inputCount} 个）`);
                } else {
                    if (task.debug) {
                        const screenshotPath = path.join(tmpCachePath, `upload-error-${Date.now()}.png`);
                        await page.screenshot({ path: screenshotPath, fullPage: true });
                        task.logger.error(`未找到上传输入框，已截图保存至: ${screenshotPath}`);
                    }
                    throw new Error("未找到视频上传输入框");
                }
            }

            // 上传视频文件
            await uploadInput.setInputFiles(params.videoPath);
            task.logger.info("视频上传成功");

            // 等待视频处理
            await page.waitForTimeout(5000);
        });

        // 上传封面
        // 页面只有一个封面上传入口，只能传横屏封面 coverPath；
        // 竖屏封面需要走 mockApi 模式（见前面的 unsupported 提示）
        if (params.coverPath) {
            task.logger.info("开始上传封面");
            await retryAction(async () => {
                // 等待封面上传区域出现
                const coverUploadBtn = await waitForElement(".cover-upload-btn", 10000);
                if (coverUploadBtn) {
                    const coverInput = page.locator('.cover-upload-btn input[type="file"]');
                    await coverInput.setInputFiles(params.coverPath);
                    task.logger.info("封面上传成功");
                    await page.waitForTimeout(2000);
                }
            });
        }

        // 填写描述：话题与 @用户拼接到文案末尾，与 mockApi 模式的文案保持一致。
        // 注意浏览器编辑器里 # 与 @ 会触发下拉选择，纯文本输入不保证绑定成结构化话题/提及，
        // 需要结构化绑定（合集、活动、内容声明等）时请走 mockApi 模式。
        const descriptionText = buildDescription({
            description: params.description,
            topics: params.topics,
            mentionedUsers: params.mentionedUsers,
        });

        if (descriptionText) {
            task.logger.info("填写视频描述");
            await retryAction(async () => {
                const descEditor = await waitForElement(".input-editor", 10000);
                if (!descEditor) {
                    throw new Error("未找到描述编辑器");
                }
                await descEditor.click();
                await page.waitForTimeout(500);

                // 清空编辑器内容
                await descEditor.evaluate((el) => {
                    el.textContent = "";
                });
                await page.waitForTimeout(300);
                task.logger.info("已清空编辑器内容");

                // 模拟用户逐字输入
                await descEditor.pressSequentially(descriptionText, { delay: 10 });
                await page.waitForTimeout(500);

                task.logger.info("描述填写完成");
            });
        }

        // 填写标题
        if (params.title) {
            task.logger.info(`填写标题: ${params.title}`);
            const title = params.title;
            await retryAction(async () => {
                const titleInput = await waitForElement(
                    "#container-wrap div.form-item-body.short-title-wrap input",
                    10000,
                );

                if (!titleInput) {
                    throw new Error("未找到标题输入框");
                }

                await titleInput.click();
                await titleInput.clear({ timeout: 3000 });
                await titleInput.fill(title, { timeout: 5000 });
                task.logger.info("标题填写完成");
            });
        }

        // 选择地点
        if (params.location) {
            task.logger.info(`选择地点: ${params.location.city}`);
            const instance = page.locator(".position-display-wrap");
            await instance.click();
            await page.waitForTimeout(1000);
            await page.locator(".location-filter-wrap input").fill(params.location.city);
            await page.waitForTimeout(2000);
            const poperInstance = page.locator(".location-filter-wrap .common-option-list-wrap .option-item");
            await poperInstance.nth(1).waitFor();
            await poperInstance.nth(1).click();
            task.logger.info("地点选择完成");
        }
        // 选择合集
        if (params.collection) {
            task.logger.info(`选择合集: ${params.collection.collectionName}`);
            const instanceCollection = page.locator(".post-album-display-wrap");
            await instanceCollection.click();
            await page.waitForTimeout(1000);
            page.locator(".post-album-wrap .option-item")
                .filter({ hasText: params.collection.collectionName })
                .first()
                .click({ force: true });
        }

        // 填写链接
        if (params.link) {
            task.logger.info(`设置扩展阅读链接: ${params.link.title}`);

            // 点击链接选择区域打开链接类型列表
            await page.locator(".post-link-wrap .link-display-wrap").click();
            await page.waitForTimeout(300);

            // 根据 urlType 选择链接类型
            const linkTypeText = params.link.urlType === 2 ? "红包封面" : "公众号文章";
            await page.locator(".link-option-item .title-wrap span").filter({ hasText: linkTypeText }).click();
            await page.waitForTimeout(300);

            // 填写链接
            const placeholder = params.link.urlType === 2 ? "粘贴红包封面链接" : "粘贴公众号文章链接";
            await page.locator(`.link-input-wrap input[placeholder="${placeholder}"]`).fill(params.link.link);
            await page.waitForTimeout(500);

            task.logger.info(`已设置${linkTypeText}: ${params.link.link}`);
        }

        // 活动选择
        if (params.event) {
            task.logger.info(`选择活动: ${params.event.eventName}`);

            // 点击活动选择区域打开活动列表
            await page.locator(".post-activity-wrap .activity-display").click();
            await page.waitForTimeout(500);

            // 在搜索框中输入活动名称
            await page
                .locator(".activity-filter-wrap .weui-desktop-form__input[placeholder='搜索活动']")
                .fill(params.event.eventName);
            await page.waitForTimeout(500);

            // 等待搜索结果加载完成
            const searchLoading = page.locator(".search-loading");
            await searchLoading.waitFor({ state: "hidden", timeout: 5000 }).catch(() => {
                task.logger.warn("活动搜索加载超时，继续尝试选择");
            });

            // 点击匹配的活动项
            const activityItem = page
                .locator(".option-item .activity-item .activity-item-info .name")
                .filter({ hasText: params.event.eventName });
            const count = await activityItem.count();
            if (count > 0) {
                await activityItem.first().click();
                await page.waitForTimeout(300);
                task.logger.info(`已选择活动: ${params.event.eventName}`);
            } else {
                task.logger.warn(`未找到活动: ${params.event.eventName}，将不参与活动`);
                // 关闭活动选择面板
                await page.locator(".post-activity-wrap .activity-display").click();
                await page.waitForTimeout(300);
            }
        }

        // 定时发布
        if (params.scheduledTime) {
            task.logger.info("设置定时发布");
            const timingRadio = page
                .locator(".weui-desktop-form__check-label")
                .filter({ hasNotText: "不定时" })
                .first();
            await timingRadio.click();
            await page.waitForTimeout(500);
            const instance = page.locator(".weui-desktop-picker__date");
            await instance.click();
            const dateD = TimeFormatter.format(params.scheduledTime * 1000, "d");
            const nowMonth = TimeFormatter.format(Date.now(), "MM月");
            const nowMonthText = TimeFormatter.format(Date.now(), "M月");
            const month = TimeFormatter.format(params.scheduledTime * 1000, "MM月");
            const monthLocator = await page
                .locator("weui-desktop-picker__panel__label")
                .filter({ hasText: month })
                .first();
            if (!monthLocator) {
                await page.locator(".weui-desktop-picker__panel__label").filter({ hasText: nowMonth }).first().click();
                await page.waitForTimeout(500);
                await page
                    .locator(".weui-desktop-picker__table-row td a")
                    .filter({ hasText: nowMonthText })
                    .first()
                    .click();
            }
            await page.waitForTimeout(500);
            await page.locator(".weui-desktop-picker__table-row td a").filter({ hasText: dateD }).first().click();

            await page
                .locator(".weui-desktop-form__input-wrp input[placeholder*='请选择时间']")
                .fill(TimeFormatter.format(params.scheduledTime * 1000, "hh:mm"));
            await page.locator("i.weui-desktop-icon__time").click();
            // 关闭弹出框
            await page.locator(".post-time-wrap .form-item .label").filter({ hasText: "发表时间" }).click();
        }

        // 视频标注
        if (params.tagInfo) {
            task.logger.info(`设置视频标注: tagType=${params.tagInfo.tagType}`);

            // 点击选择器打开下拉菜单
            await page.locator(".mark-tag-select").click();
            await page.waitForTimeout(300);

            // 根据 tagType 选择对应的选项
            const tagTypeTextMap: Record<number, string> = {
                0: "无需标注",
                1: "含AI生成内容",
                2: "内容包含营销广告",
                3: "内容为虚构剧情，仅供娱乐",
                5: "内容为自行拍摄",
                7: "内容为转载",
                8: "个人观点，仅供参考",
            };

            const tagText = tagTypeTextMap[params.tagInfo.tagType];
            if (!tagText) {
                task.logger.warn(`未知的 tagType: ${params.tagInfo.tagType}，跳过标注设置`);
            } else {
                // 点击对应的选项
                await page.locator(".mark-tag-option .option-main").filter({ hasText: tagText }).click();
                await page.waitForTimeout(300);

                // tagType=5 自行拍摄：需要填写拍摄时间和地点
                if (params.tagInfo.tagType === 5) {
                    const shootInfo = (params.tagInfo as any).shootInfo;
                    if (shootInfo) {
                        task.logger.info("填写拍摄时间和地点...");
                        await page.waitForTimeout(500);

                        // 填写拍摄时间
                        if (shootInfo.postTimestamp) {
                            task.logger.info(`设置拍摄时间: ${shootInfo.postTimestamp}`);
                            const timestamp = parseInt(shootInfo.postTimestamp, 10) * 1000;
                            const date = new Date(timestamp);

                            // 点击日期选择器
                            await page
                                .locator(
                                    ".original-dialog-content .weui-desktop-picker__date input[placeholder*='请选择拍摄时间']",
                                )
                                .click();
                            await page.waitForTimeout(300);

                            // 选择日期（直接点击日历中对应的日期）
                            const dayNum = date.getDate();
                            await page
                                .locator(".weui-desktop-picker__table a")
                                .filter({ hasText: new RegExp(`^\\s*${dayNum}\\s*$`) })
                                .first()
                                .click();
                            await page.waitForTimeout(300);
                        }

                        // 填写拍摄地点
                        if (shootInfo.countryCode || shootInfo.provinceCode || shootInfo.cityCode) {
                            task.logger.info("设置拍摄地点...");
                            // 点击地点选择器
                            await page
                                .locator(
                                    ".original-dialog-content .weui-desktop-form__dropdowncascade .weui-desktop-form__dropdowncascade__dt",
                                )
                                .click();
                            await page.waitForTimeout(300);

                            // 选择国家（默认中国 countryCode=1156）
                            if (shootInfo.countryCode === 1156) {
                                await page
                                    .locator(".weui-desktop-dropdown__list-ele .weui-desktop-dropdown__list-ele__text")
                                    .filter({ hasText: "中国" })
                                    .click();
                                await page.waitForTimeout(300);

                                // TODO: 传入参数添加标签数组，根据标签层层递进，用 hasText 选择对应的省市，例如shootInfo.locationTags = ["安徽省", "合肥市"]，然后循环选择

                                task.logger.warn("RPA 模式下暂不支持选择具体省份和城市，仅选择了国家");
                            } else {
                                task.logger.warn(`不支持的国家代码: ${shootInfo.countryCode}，跳过地点设置`);
                            }
                        }

                        // 点击确认按钮关闭弹窗
                        const confirmBtn = await waitForElement(".weui-desktop-dialog .weui-desktop-btn_primary");
                        if (confirmBtn) {
                            await confirmBtn.click();
                            await page.waitForTimeout(300);
                        }
                    } else {
                        task.logger.warn("tagType=5 需要提供 shootInfo 字段（拍摄时间和地点）");
                    }
                }

                // tagType=7 转载：需要填写转载来源
                if (params.tagInfo.tagType === 7) {
                    const repostSource = (params.tagInfo as any).repostSource;
                    if (repostSource) {
                        task.logger.info(`填写转载来源: ${repostSource}`);
                        await page.waitForTimeout(500);

                        // 填写转载来源
                        await page.locator(".repost-dialog-content .repost-textarea").fill(repostSource);
                        await page.waitForTimeout(300);

                        // 点击完成按钮关闭弹窗
                        const confirmBtn = await waitForElement(".weui-desktop-dialog .weui-desktop-btn_primary");
                        if (confirmBtn) {
                            await confirmBtn.click();
                            await page.waitForTimeout(300);
                        }
                    } else {
                        task.logger.warn("tagType=7 需要提供 repostSource 字段（转载来源）");
                    }
                }
            }
        }

        task.logger.info("准备发布...");
        await page.waitForTimeout(500);

        let videoId = "";
        // 定义响应处理函数
        const handleResponse = async (response: Response) => {
            const url = response.url();
            if (url.includes("/post/post_create")) {
                const jsonResponse = await response.json();
                page.off("response", handleResponse);
                videoId = jsonResponse.object?.id || jsonResponse.data?.objectId || "";
            }
        };

        // 开始监听响应事件
        page.on("response", handleResponse);
        task._timerRecord.PrePublish = Date.now();

        // 等待视频上传完成：上传结束后，预览区的媒体操作栏才会渲染出该节点。
        // 页面是 wujie 微前端，内容在 <wujie-app> 的 shadowRoot 里，普通 selector 穿不进去，
        // 需要在 evaluate 里手动跨 shadow 边界查询。
        const MEDIA_OPR_SELECTOR =
            "#container-wrap > div.container-center > div > div > div.main-body-wrap.post-create > div.main-body > div > " +
            "div.post-edit-wrap.material-edit-wrap > div.material > div.preview > div > div.media-opr > div > span > div > div > div";

        task.logger.info("等待视频上传完成（预览区媒体操作栏出现）...");

        const uploadTimeout = 10 * 60 * 1000; // 最多等 10 分钟
        const uploadWaitStart = Date.now();
        let lastProgressLog = 0;

        while (true) {
            const ready = await page.evaluate((selector) => {
                const host = document.querySelector("#container-wrap > div.container-center > div > wujie-app");
                const root = (host as HTMLElement | null)?.shadowRoot;
                if (!root) return false;
                return !!root.querySelector(selector);
            }, MEDIA_OPR_SELECTOR);

            if (ready) break;

            const elapsed = Date.now() - uploadWaitStart;
            if (elapsed > uploadTimeout) {
                throw new Error(`视频上传超时，预览区未就绪（已等待 ${Math.round(elapsed / 1000)}s）`);
            }

            // 每 15s 打印一次进度，避免日志刷屏
            if (elapsed - lastProgressLog >= 15000) {
                lastProgressLog = elapsed;
                task.logger.info(`视频仍在上传中... 已等待 ${Math.round(elapsed / 1000)}s`);
            }

            await page.waitForTimeout(1000);
        }

        task.logger.info(`视频上传完成（等待 ${Math.round((Date.now() - uploadWaitStart) / 1000)}s）`);

        // 点击发布按钮（同样需要跨 shadow 边界）
        const clicked = await page.evaluate(() => {
            const host = document.querySelector("#container-wrap > div.container-center > div > wujie-app");
            const root = (host as HTMLElement | null)?.shadowRoot;
            if (!root) return "未找到 wujie-app shadowRoot";

            const btns = Array.from(root.querySelectorAll(".form-btns .weui-desktop-btn"));
            const target = btns.find((el) => (el.textContent || "").includes("发表")) as HTMLElement | undefined;
            if (!target) return "未找到发表按钮";
            if (target.classList.contains("weui-desktop-btn_disabled")) return "发表按钮仍处于禁用状态";

            target.click();
            return null;
        });

        if (clicked) {
            throw new Error(clicked);
        }
        task.logger.info("已点击发布按钮，等待响应...");

        // 等待页面跳转，表示发布完成
        try {
            await page.waitForURL((url) => url.href !== page.url(), { timeout: 30000 });
            task.logger.info(`发布成功，页面已跳转: ${page.url()}`);
        } catch {
            task.logger.warn("等待页面跳转超时，可能发布失败或网络慢，继续关闭页面");
        }

        // 关闭页面
        await page.close();
        return success(videoId, "发布成功");
    } catch (error) {
        let errorMsg = error instanceof Error ? error.message : String(error);
        if (errorMsg.includes("context or browser has been closed")) {
            errorMsg = "浏览器上下文已被关闭";
        }

        task.logger.error(`微信视频号视频发布失败: ${errorMsg}`);
        return response(414, `微信视频号视频发布失败: ${errorMsg}`, "");
    }
};
